import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { useRef, useState } from 'react';
import { Tooltip } from 'primereact/tooltip';
import * as Yup from 'yup';

import { BrCalendar } from './BrCalendar';
import { ErrorValidationMessage } from './ErrorValidationMessage';

import '../layout/sass/_form.scss';
import { Checkbox } from 'primereact/checkbox';

export const FormAplicacaoProdutosQuimicos = ({ isEditing, isLoading, visible, setVisible, aplicacaoProdutosQuimicos, setAplicacaoProdutosQuimicos }) => {

    const toast = useRef(null);
    const [isSubmiting, setIsSubmiting] = useState(false);
    const formaDose = [
        { label: 'KG_M', value: 'KG_M' },
        { label: 'L_M', value: 'L_M' },
        { label: 'G_PLANTA', value: 'G_PLANTA' },
    ];
    const formaDoseMapping = {
        'KG_M': 'Kg/m²',
        'L_M': 'L/m²',
        'G_PLANTA': 'g/Planta'
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        formik.setFieldValue(name, checked);

        if (name === 'naoNecessarias' && checked) {
            formik.setFieldValue('data', '');
            formik.setFieldValue('periodoCarencia', '');
            formik.setFieldValue('pragaDoencaAlvo', '');
            formik.setFieldValue('nomeProduto', '');
            formik.setFieldValue('dose', '');
            formik.setFieldValue('formaDose', null);
            formik.setFieldValue('volumeCalda', '');
            formik.setFieldValue('operador', '');
        }
    };

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const schema = Yup.object().shape({
        naoNecessarias: Yup.boolean(),
        data: Yup
            .date()
            .typeError("Uma data deve ser inserida.")
            .when('naoNecessarias', {
                is: false,
                then: Yup.date().required("A data é obrigatória."),
            }),
        periodoCarencia: Yup
            .number()
            .transform((value, originalValue) => {
                return isNaN(originalValue) ? 0 : Number(originalValue);
            })
            .integer("A quantidade de dias deve ser um número inteiro.")
            .min(1, "A quantidade de dias deve ser maior que 0.")
            .when('naoNecessarias', {
                is: false,
                then: Yup.number().required("A quantidade de dias é obrigatória."),
            }),
        pragaDoencaAlvo: Yup
            .string()
            .when('naoNecessarias', {
                is: false,
                then: Yup.string().required("O nome da Praga/Doença Alvo é obrigatório."),
            })
            .max(200, "O nome da Praga/Doença Alvo deve ter no máximo 200 caracteres."),
        nomeProduto: Yup
            .string()
            .when('naoNecessarias', {
                is: false,
                then: Yup.string().required("O nome do produto é obrigatório."),
            })
            .max(200, "O nome do produto deve ter no máximo 200 caracteres."),
        dose: Yup
            .number()
            .transform((value, originalValue) => {
                return isNaN(originalValue) ? 0 : Number(originalValue);
            })
            .min(1, "A quantidade da dose deve ser maior que 0.")
            .when('naoNecessarias', {
                is: false,
                then: Yup.number().required("A dose é obrigatória."),
            }),
        formaDose: Yup
            .mixed()
            .nullable()
            .when('naoNecessarias', {
                is: false,
                then: Yup.mixed().required("É obrigatório informar a unidade de medida da dose."),
            }),
        volumeCalda: Yup
            .number()
            .transform((value, originalValue) => {
                return isNaN(originalValue) ? 0 : Number(originalValue);
            })
            .min(1, "A quantidade deve ser maior que 0.")
            .when('naoNecessarias', {
                is: false,
                then: Yup.number().required("A quantidade é obrigatória."),
            }),
        operador: Yup
            .string()
            .when('naoNecessarias', {
                is: false,
                then: Yup.string().required("O nome do Operador é obrigatório."),
            })
            .max(200, "O nome do Operador deve ter no máximo 200 caracteres."),
    });

    const formik = useFormik({

        initialValues: {
            naoNecessarias: false,
            "data": "",
            "periodoCarencia": "",
            "pragaDoencaAlvo": "",
            "nomeProduto": "",
            "dose": "",
            "formaDose": null,
            "volumeCalda": "",
            "operador": ""
        },

        validationSchema: schema,

        enableReinitialize: false,

        onSubmit: (data) => {
            setIsSubmiting(true);

            setAplicacaoProdutosQuimicos(aplicacaoProdutosQuimicos => [...aplicacaoProdutosQuimicos, data]);

            setIsSubmiting(false);
        }
    })

    return (
        <div className="card p-grid p-col-12 p-mx-0 p-mt-0">
            <Toast ref={toast} position="bottom-right" />
            <div className="p-col-12 p-ai-center">
                <form onSubmit={formik.handleSubmit}>
                    <div className="p-grid p-formgrid p-fluid p-mx-0">
                        <div className="p-grid p-col-11">
                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px', display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    id="naoNecessarias"
                                    name="naoNecessarias"
                                    checked={formik.values.naoNecessarias}
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="naoNecessarias" className={classNames('p-ml-2', { 'p-error': isFormFieldValid('naoNecessarias') })}>
                                    Não são necessárias
                                </label>
                            </div>
                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px' }}>
                                <label
                                    htmlFor="data"
                                    className={classNames({ 'p-error': isFormFieldValid('data') })}
                                >
                                    Data
                                </label>
                                <div style={{ pointerEvents: formik.values.naoNecessarias ? 'none' : 'auto', opacity: formik.values.naoNecessarias ? 0.6 : 1 }}>
                                    <BrCalendar
                                        id="data"
                                        name="data"
                                        value={formik.values.data}
                                        onChange={formik.handleChange}
                                        placeholder={""}
                                        className={classNames({ 'p-invalid': isFormFieldValid('data') })}
                                        yearRange={`${new Date().getFullYear() - 4}:${new Date().getFullYear() + 1}`}
                                        disabledDays="none"
                                        readOnly={formik.values.naoNecessarias}
                                    />
                                </div>
                                {formik.errors.data && formik.touched.data &&
                                    <ErrorValidationMessage message={formik.errors.data} />
                                }
                            </div>

                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px' }}>
                                <label htmlFor="periodoCarencia" className={classNames({ 'p-error': isFormFieldValid('periodoCarencia') })}>
                                    Período de Carência
                                </label>
                                <Tooltip
                                    target=".periodoCarencia"
                                    position="bottom"
                                    event="both"
                                    autoHide={false}
                                    style={{
                                        width: '150px',
                                        fontSize: '14px',
                                        padding: '10px',
                                    }}
                                    disabled={formik.values.naoNecessarias}
                                >
                                    Medido em dias.
                                </Tooltip>
                                {!isLoading ? (
                                    <InputNumber
                                        id="periodoCarencia"
                                        name="periodoCarencia"
                                        className={classNames({ 'p-invalid': isFormFieldValid('periodoCarencia'), 'periodoCarencia': true })}
                                        value={formik.values.periodoCarencia}
                                        onValueChange={formik.handleChange}
                                        disabled={formik.values.naoNecessarias}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                                {formik.errors.periodoCarencia && formik.touched.periodoCarencia && (
                                    <ErrorValidationMessage message={formik.errors.periodoCarencia} />
                                )}
                            </div>

                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px' }}>
                                <label htmlFor="pragaDoencaAlvo" className={classNames({ 'p-error': isFormFieldValid('pragaDoencaAlvo') })}>
                                    Praga ou Doença Alvo
                                </label>
                                {!isLoading ? (
                                    <InputText
                                        id="pragaDoencaAlvo"
                                        name="pragaDoencaAlvo"
                                        className={classNames({ 'p-invalid': isFormFieldValid('pragaDoencaAlvo') })}
                                        value={formik.values.pragaDoencaAlvo}
                                        onChange={formik.handleChange}
                                        disabled={formik.values.naoNecessarias}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                                {formik.errors.pragaDoencaAlvo && formik.touched.pragaDoencaAlvo && (
                                    <ErrorValidationMessage message={formik.errors.pragaDoencaAlvo} />
                                )}
                            </div>

                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px' }}>
                                <label htmlFor="nomeProduto" className={classNames({ 'p-error': isFormFieldValid('nomeProduto') })}>
                                    Nome do Produto
                                </label>
                                {!isLoading ? (
                                    <InputText
                                        id="nomeProduto"
                                        name="nomeProduto"
                                        className={classNames({ 'p-invalid': isFormFieldValid('nomeProduto') })}
                                        value={formik.values.nomeProduto}
                                        onChange={formik.handleChange}
                                        disabled={formik.values.naoNecessarias}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                                {formik.errors.nomeProduto && formik.touched.nomeProduto && (
                                    <ErrorValidationMessage message={formik.errors.nomeProduto} />
                                )}
                            </div>

                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px' }}>
                                <label htmlFor="dose" className={classNames({ 'p-error': isFormFieldValid('dose') })}>
                                    Dose
                                </label>
                                <Tooltip
                                    target=".dose"
                                    position="bottom"
                                    event="both"
                                    autoHide={false}
                                    style={{
                                        width: '200px',
                                        fontSize: '14px',
                                        padding: '10px',
                                    }}
                                    disabled={formik.values.naoNecessarias}
                                >
                                    Selecione uma unidade de medida ao lado.
                                </Tooltip>
                                {!isLoading ? (
                                    <InputNumber
                                        id="dose"
                                        name="dose"
                                        className={classNames({ 'p-invalid': isFormFieldValid('dose'), 'dose': true })}
                                        value={formik.values.dose}
                                        onValueChange={formik.handleChange}
                                        disabled={formik.values.naoNecessarias}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                                {formik.errors.dose && formik.touched.dose && (
                                    <ErrorValidationMessage message={formik.errors.dose} />
                                )}
                            </div>

                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px' }}>
                                <label htmlFor="formaDose" className={classNames({ 'p-error': isFormFieldValid('formaDose') })}>
                                    Unidade da Dose
                                </label>
                                {!isLoading ? (
                                    <Dropdown
                                        id="formaDose"
                                        name="formaDose"
                                        options={formaDose.map(option => ({
                                            label: formaDoseMapping[option.value],
                                            value: option.value
                                        }))}
                                        className={classNames({ 'p-invalid': isFormFieldValid('formaDose'), 'formaDose': true })}
                                        value={formik.values.formaDose}
                                        onChange={formik.handleChange}
                                        disabled={formik.values.naoNecessarias}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                                {formik.errors.formaDose && formik.touched.formaDose && (
                                    <ErrorValidationMessage message={formik.errors.formaDose} />
                                )}
                            </div>

                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px' }}>
                                <label htmlFor="volumeCalda" className={classNames({ 'p-error': isFormFieldValid('volumeCalda') })}>
                                    Volume de Calda
                                </label>
                                <Tooltip
                                    target=".volumeCalda"
                                    position="bottom"
                                    event="both"
                                    autoHide={false}
                                    style={{
                                        width: '150px',
                                        fontSize: '14px',
                                        padding: '10px',
                                    }}
                                    disabled={formik.values.naoNecessarias}
                                >
                                    Medido em L/ha.
                                </Tooltip>
                                {!isLoading ? (
                                    <InputNumber
                                        id="volumeCalda"
                                        name="volumeCalda"
                                        className={classNames({ 'p-invalid': isFormFieldValid('volumeCalda'), 'volumeCalda': true })}
                                        value={formik.values.volumeCalda}
                                        onValueChange={formik.handleChange}
                                        disabled={formik.values.naoNecessarias}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                                {formik.errors.volumeCalda && formik.touched.volumeCalda && (
                                    <ErrorValidationMessage message={formik.errors.volumeCalda} />
                                )}
                            </div>

                            <div className="p-field p-col-6 p-sm-4 p-md-4" style={{ padding: '0 3px' }}>
                                <label htmlFor="operador" className={classNames({ 'p-error': isFormFieldValid('operador') })}>
                                    Operador
                                </label>
                                {!isLoading ? (
                                    <InputText
                                        id="operador"
                                        name="operador"
                                        className={classNames({ 'p-invalid': isFormFieldValid('operador') })}
                                        value={formik.values.operador}
                                        onChange={formik.handleChange}
                                        disabled={formik.values.naoNecessarias}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                                {formik.errors.operador && formik.touched.operador && (
                                    <ErrorValidationMessage message={formik.errors.operador} />
                                )}
                            </div>
                        </div>
                        <div className="p-grid p-col-1">
                            <div className="p-d-flex p-jc-end" style={{ width: '100px', padding: '0 2px' }}>
                                <Button
                                    icon="pi pi-plus"
                                    type="button"
                                    iconPos="right"
                                    onClick={formik.handleSubmit}
                                    loading={isSubmiting}
                                    style={{ width: '40px' }}
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
